import { useEffect, useMemo, useState } from 'react';

import { api } from '../api/http.js';
import { useAdminAuth } from '../auth/AdminAuthContext.jsx';
import { useUI } from '../ui/UIProvider.jsx';
import { Icon } from '../components/icons.jsx';
import { RichTextEditor } from '../components/RichTextEditor.jsx';

/**
 * Broadcast push notifications — one-way blasts to every user (or a
 * tier-filtered subset). Used for feature-release announcements,
 * outage notices, "we're back online", etc. Replies are NOT
 * possible — the client side identifies broadcasts by data.kind ==
 * "broadcast" and routes the tap to data.url if set, or just
 * dismisses.
 *
 * Compose form on top + history table below. Superadmin-only on
 * the send button; lower roles see the history but can't fire.
 *
 * @author <lukman@zshift.io>
 */
export function Broadcasts() {
  const { me } = useAdminAuth();
  const { toast, confirm } = useUI();

  const [title,     setTitle]     = useState('');
  const [body,      setBody]      = useState('');
  const [audience,  setAudience]  = useState('ALL');
  // Deep-link picker. `urlPreset` keys into URL_PRESETS for the
  // pre-baked destinations; `urlCustom` only matters when preset is
  // 'custom' (admin pasted their own). The final wire value is
  // derived from both via the `url` memo below.
  const [urlPreset, setUrlPreset] = useState('none');
  const [urlCustom, setUrlCustom] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [sending,   setSending]   = useState(false);
  const url = useMemo(() => {
    if (urlPreset === 'none')   return '';
    if (urlPreset === 'custom') return urlCustom.trim();
    return URL_PRESETS.find((p) => p.key === urlPreset)?.url || '';
  }, [urlPreset, urlCustom]);

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSuperadmin = me?.role === 'SUPERADMIN';

  const TITLE_MAX = 120;
  // Cap on raw HTML length. Higher than the old 2000-char limit because
  // formatting + inline-image <img> tags eat characters fast. We still
  // show a visible-text counter under the editor so the admin sees the
  // user-perceived length, not the wire size.
  const BODY_MAX  = 20000;
  // Visible-text counter — strips HTML so headings and bullets don't
  // count their tags against the human-facing word/char budget.
  const bodyVisibleText = useMemo(() => stripHtmlClient(body), [body]);

  // ── History fetch ─────────────────────────────────────────────
  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api('/api/admin/broadcasts');
      setHistory(res?.items || []);
    } catch (e) {
      console.warn(e);
      toast.error(e.message || 'Could not load broadcast history.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-line */ }, []);

  // ── Send ──────────────────────────────────────────────────────
  // Editor outputs HTML; a "blank" doc still serialises to "<p></p>"
  // so we measure visible-text length, not raw HTML, when deciding
  // whether the form is fillable.
  // Only validate the URL shape when the admin's in custom-mode —
  // preset entries are hardcoded above and always well-formed.
  const customUrlValid = urlPreset !== 'custom'
                      || (url && /^https?:\/\//i.test(url));
  const canSend = title.trim().length > 0
               && bodyVisibleText.length > 0
               && title.length  <= TITLE_MAX
               && body.length   <= BODY_MAX
               && customUrlValid;

  const audienceLabel = useMemo(() => ({
    ALL:          'every user',
    BASIC_ONLY:   'every Basic user',
    PREMIUM_ONLY: 'every Premium user',
  }[audience] || 'users'), [audience]);

  const send = async () => {
    if (!canSend || !isSuperadmin) return;
    const emailNote = sendEmail
      ? ' It will also be emailed to every user in this audience.'
      : '';
    const ok = await confirm({
      title: 'Send this broadcast?',
      message: `This will push "${title.trim()}" to ${audienceLabel}.${emailNote} The notification cannot be recalled once sent.`,
      confirmLabel: sendEmail ? 'Push + Email' : 'Send broadcast',
    });
    if (!ok) return;
    setSending(true);
    try {
      const res = await api('/api/admin/broadcasts', {
        method: 'POST',
        body: {
          title:     title.trim(),
          body:      body.trim(),
          audience,
          url:       url.trim() || null,
          sendEmail,
        },
      });
      // Compose the toast from both channels — push counts always,
      // email counts only when the operator opted in.
      const pushLine = `Push: ${res.successful}/${res.sentToDevices} delivered` +
                      (res.failed > 0 ? ` (${res.failed} failed)` : '');
      const emailLine = res.emailed
        ? ` · Email: ${res.emailsSuccessful}/${res.emailsAttempted} sent` +
          (res.emailsFailed > 0 ? ` (${res.emailsFailed} bounced)` : '')
        : '';
      toast.success(pushLine + emailLine);
      setTitle(''); setBody(''); setUrlPreset('none'); setUrlCustom(''); setSendEmail(false);
      refresh();
    } catch (e) {
      toast.error(e.message || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Broadcast <em>push</em></h1>
          <p className="page-sub">
            One-way notifications to every user, or a tier-filtered subset.
            Replies aren't possible — use this for feature releases, outage notices,
            and product updates. Every send is logged.
          </p>
        </div>
      </div>

      {/* ── Compose ─────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 22 }}>
        <div className="card-head">
          <h3>Compose</h3>
          <div className="sub">
            {isSuperadmin ? 'SUPERADMIN ONLY' : 'READ-ONLY · SUPERADMIN REQUIRED'}
          </div>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Audience picker */}
          <div>
            <label className="form-label">Audience</label>
            <div className="seg" style={{ marginTop: 6 }}>
              <button
                className={audience === 'ALL' ? 'on' : ''}
                onClick={() => setAudience('ALL')}
                disabled={!isSuperadmin}
              >All users</button>
              <button
                className={audience === 'BASIC_ONLY' ? 'on' : ''}
                onClick={() => setAudience('BASIC_ONLY')}
                disabled={!isSuperadmin}
              >Basic only</button>
              <button
                className={audience === 'PREMIUM_ONLY' ? 'on' : ''}
                onClick={() => setAudience('PREMIUM_ONLY')}
                disabled={!isSuperadmin}
              >Premium only</button>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="form-label" htmlFor="bc-title">
              Title
              <small style={{ marginLeft: 8, color: title.length > TITLE_MAX ? 'var(--red)' : 'var(--ink-3)', fontFamily: 'Geist Mono, monospace', fontSize: 11 }}>
                {title.length} / {TITLE_MAX}
              </small>
            </label>
            <input
              id="bc-title"
              className="form-input"
              type="text"
              maxLength={TITLE_MAX}
              placeholder="Daily review email is live!"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!isSuperadmin}
            />
          </div>

          {/* Body — WYSIWYG editor. The push channel strips HTML to
              plain text server-side, the email channel renders the
              HTML directly inside the templated wrapper. */}
          <div>
            <label className="form-label">
              Body
              <small style={{ marginLeft: 8, color: bodyVisibleText.length > BODY_MAX ? 'var(--red)' : 'var(--ink-3)', fontFamily: 'Geist Mono, monospace', fontSize: 11 }}>
                {bodyVisibleText.length.toLocaleString()} chars visible
              </small>
            </label>
            <RichTextEditor
              value={body}
              onChange={setBody}
              disabled={!isSuperadmin}
              placeholder="Opt in from Settings → Calendar to get a daily morning email summarising today's reminders and overdue items."
            />
            <small style={{ display: 'block', marginTop: 4, color: 'var(--ink-3)', fontSize: 12 }}>
              Formatting and inline images show up in the email channel.
              Push notifications strip everything down to plain text — keep
              the first 200 characters readable as a one-line toast.
            </small>
          </div>

          {/* Deep-link picker. Most broadcasts point at one of a handful
              of routable destinations — exposing those as a curated
              dropdown removes a class of typos (broken `?settings=` vs
              `?openSettings=`, wrong host, etc.) and saves the admin
              from looking them up. "Custom URL" reveals a free-text
              fallback for anything not in the preset list. */}
          <div>
            <label className="form-label" htmlFor="bc-url-preset">
              Tap destination
            </label>
            <select
              id="bc-url-preset"
              className="form-input"
              value={urlPreset}
              onChange={(e) => setUrlPreset(e.target.value)}
              disabled={!isSuperadmin}
            >
              {URL_PRESET_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {urlPreset === 'custom' && (
              <input
                className="form-input"
                type="url"
                placeholder="https://app.noteshq.ai/?settings=calendar"
                value={urlCustom}
                onChange={(e) => setUrlCustom(e.target.value)}
                disabled={!isSuperadmin}
                style={{ marginTop: 8 }}
              />
            )}
            <small style={{ display: 'block', marginTop: 6, color: 'var(--ink-3)', fontSize: 12 }}>
              {urlPreset === 'none'
                ? 'The notification just dismisses on tap.'
                : urlPreset === 'custom'
                  ? 'Paste any absolute URL. Both noteshq.ai and app.noteshq.ai are valid.'
                  : <>Tapping opens <code style={{ background: 'var(--surface-2)', padding: '1px 5px', borderRadius: 4 }}>{url}</code></>
              }
            </small>
          </div>

          {/* Live preview */}
          <BroadcastPreview title={title} body={body} url={url} />

          {/* Also-by-email opt-in. Defaults off — every broadcast already
              fires a push; email is the louder, persistent variant we
              reserve for things worth landing in someone's inbox. The
              footnote sets expectation about reach + delivery time. */}
          <label
            className="form-check"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '10px 12px',
              background: 'var(--surface-2)',
              border: '1px solid var(--line)',
              borderRadius: 8,
              cursor: isSuperadmin ? 'pointer' : 'not-allowed',
              opacity: isSuperadmin ? 1 : 0.6,
            }}
          >
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              disabled={!isSuperadmin}
              style={{ marginTop: 2 }}
            />
            <span style={{ flex: 1, fontSize: 13, lineHeight: 1.45 }}>
              <strong style={{ display: 'block', marginBottom: 2 }}>Also send by email</strong>
              <span style={{ color: 'var(--ink-3)' }}>
                Sends an HTML email to {audienceLabel} alongside the push.
                Useful for release announcements; noisy for everyday updates.
              </span>
            </span>
          </label>

          {/* Send row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
            <button
              className="btn primary"
              onClick={send}
              disabled={!canSend || sending || !isSuperadmin}
              title={isSuperadmin ? `Push to ${audienceLabel}${sendEmail ? ' + email' : ''}` : 'Superadmin role required'}
            >
              <Icon name="send" size={14} />
              {sending
                ? 'Sending…'
                : `Send to ${audienceLabel}${sendEmail ? ' (push + email)' : ''}`}
            </button>
          </div>
        </div>
      </div>

      {/* ── History ─────────────────────────────────────────── */}
      <div className="card">
        <div className="card-head">
          <h3>Recent broadcasts</h3>
          <button className="btn sm ghost" onClick={refresh}>
            <Icon name="refresh" size={13} /> Refresh
          </button>
        </div>
        <div className="table-wrap">
          <table className="t">
            <thead>
              <tr>
                <th>Sent</th>
                <th>Title / Body</th>
                <th>Audience</th>
                <th>By</th>
                <th style={{ textAlign: 'right' }}>Delivery</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</td></tr>
              )}
              {!loading && history.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
                  No broadcasts sent yet.
                </td></tr>
              )}
              {!loading && history.map((h) => (
                <tr key={h.id}>
                  <td className="mono" style={{ color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                    {agoString(h.createdAt)}
                  </td>
                  <td style={{ minWidth: 280 }}>
                    <strong style={{ display: 'block', fontSize: 13.5 }}>{h.title}</strong>
                    {/* History rows can have HTML bodies post-WYSIWYG —
                        strip to plain text for the inline preview so
                        we don't see raw tags. Older rows are already
                        plain text and pass through untouched. */}
                    <span style={{
                      display: 'block',
                      color: 'var(--ink-3)',
                      fontSize: 12,
                      lineHeight: 1.4,
                      maxWidth: 480,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {stripHtmlClient(h.body || '')}
                    </span>
                  </td>
                  <td>
                    <span className={`pill ${audiencePillClass(h.audience)}`}>
                      <span className="dot"/> {audienceLabel2(h.audience)}
                    </span>
                  </td>
                  <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {h.sentByEmail || '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {/* Push counts. Always present — every broadcast pushes. */}
                    <div title="Push notifications">
                      <small style={{ color: 'var(--ink-3)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        push
                      </small>
                      {' '}
                      <span style={{ color: 'var(--green)', fontWeight: 600 }}>
                        {h.successful?.toLocaleString() ?? 0}
                      </span>
                      <span style={{ color: 'var(--ink-3)' }}>
                        {' / '}
                        {h.sentToDevices?.toLocaleString() ?? 0}
                      </span>
                      {h.failed > 0 && (
                        <span style={{ color: 'var(--red)', fontSize: 11, marginLeft: 6 }}>
                          ({h.failed} failed)
                        </span>
                      )}
                    </div>
                    {/* Email counts. Only when the admin opted in for
                        that send — otherwise hidden so the column stays
                        compact for the common case. */}
                    {h.emailed && (
                      <div title="Emails" style={{ marginTop: 2 }}>
                        <small style={{ color: 'var(--ink-3)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          email
                        </small>
                        {' '}
                        <span style={{ color: 'var(--green)', fontWeight: 600 }}>
                          {h.emailsSuccessful?.toLocaleString() ?? 0}
                        </span>
                        <span style={{ color: 'var(--ink-3)' }}>
                          {' / '}
                          {h.emailsAttempted?.toLocaleString() ?? 0}
                        </span>
                        {h.emailsFailed > 0 && (
                          <span style={{ color: 'var(--red)', fontSize: 11, marginLeft: 6 }}>
                            ({h.emailsFailed} bounced)
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Preview of the notification card as users will see it ───────
function BroadcastPreview({ title, body, url }) {
  return (
    <div style={{ marginTop: 4 }}>
      <small style={{ color: 'var(--ink-3)', fontSize: 11, fontFamily: 'Geist Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Preview
      </small>
      <div className="broadcast-preview">
        <div className="broadcast-preview-icon">
          <svg width="16" height="16" viewBox="0 0 100 100" aria-hidden="true">
            <rect x="14" y="12" width="58" height="58" rx="11" fill="#B7C2F3"/>
            <rect x="18" y="18" width="58" height="58" rx="11" fill="#8092E5"/>
            <rect x="22" y="24" width="58" height="58" rx="11" fill="#2D43E0"/>
            <circle cx="75" cy="25" r="13" fill="#FF6B47"/>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong style={{ display: 'block', fontSize: 13.5 }}>
            {title.trim() || 'Notification title'}
          </strong>
          {/* The OS toast a user actually sees is plain-text only — we
              feed it the visible text from the WYSIWYG output so the
              preview matches what FCM will display. The full HTML body
              is rendered separately below as the "email preview". */}
          <span style={{
            display: 'block',
            fontSize: 12.5,
            color: 'var(--ink-2)',
            lineHeight: 1.4,
            marginTop: 2,
            whiteSpace: 'pre-wrap',
          }}>
            {stripHtmlClient(body).trim() || 'Notification body shows up here, two-line snippet.'}
          </span>
          {url && /^https?:\/\//i.test(url.trim()) && (
            <span className="mono" style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-3)', marginTop: 4 }}>
              ↪ {url.trim()}
            </span>
          )}
        </div>
        <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 10, color: 'var(--ink-3)' }}>now</span>
      </div>
    </div>
  );
}

// ── Local helpers ──────────────────────────────────────────────
function audiencePillClass(a) {
  if (a === 'PREMIUM_ONLY') return 'plus';
  if (a === 'BASIC_ONLY')   return 'free';
  return 'info';
}
function audienceLabel2(a) {
  if (a === 'PREMIUM_ONLY') return 'PREMIUM';
  if (a === 'BASIC_ONLY')   return 'BASIC';
  return 'ALL';
}
function agoString(iso) {
  const ts = new Date(iso).getTime();
  if (!ts) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1) return 'now';
  if (mins < 60) return mins + 'm ago';
  const h = Math.floor(mins / 60);
  if (h < 24) return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 30) return d + 'd ago';
  return Math.floor(d / 30) + 'mo ago';
}

// ── Deep-link presets ──────────────────────────────────────────────
// Curated list of destinations the web app + marketing site know how
// to handle. Keep these aligned with the URL-param handlers in
// noteshq-web/src/App.jsx — if you add a new `?something=` route
// there, add a preset here so the admin can target it without typing
// the URL by hand.
//
// Two groups so the dropdown stays scannable as the list grows:
//   - "Inside the app"     → URLs on app.noteshq.ai
//   - "Marketing pages"    → URLs on noteshq.ai
//
// The `key` is what we persist in component state; the `url` is what
// goes on the wire and into the click_action FCM data field.
const URL_PRESET_GROUPS = [
  {
    label: 'No deep link',
    options: [
      { key: 'none', label: '— No link (notification just dismisses) —', url: '' },
    ],
  },
  {
    label: 'Inside the app',
    options: [
      { key: 'home',           label: 'Open the app (home)',                 url: 'https://app.noteshq.ai/' },
      { key: 'settings-plan',  label: 'Settings → Plan / Upgrade',           url: 'https://app.noteshq.ai/?openSettings=plan' },
      { key: 'settings-cal',   label: 'Settings → Calendar & digest',        url: 'https://app.noteshq.ai/?openSettings=calendar' },
      { key: 'settings-data',  label: 'Settings → Data (export / import)',   url: 'https://app.noteshq.ai/?openSettings=data' },
      { key: 'settings-langs', label: 'Settings → Languages',                url: 'https://app.noteshq.ai/?openSettings=languages' },
      { key: 'settings',       label: 'Settings (general)',                  url: 'https://app.noteshq.ai/?openSettings=true' },
    ],
  },
  {
    label: 'Marketing pages',
    options: [
      { key: 'pricing',  label: 'Pricing',     url: 'https://noteshq.ai/pricing.html' },
      { key: 'help',     label: 'Help center', url: 'https://noteshq.ai/helpcenter.html' },
      { key: 'pebble',   label: 'Pebble (companion device)', url: 'https://noteshq.ai/pebble.html' },
      { key: 'home-mkt', label: 'Marketing home page',       url: 'https://noteshq.ai/' },
    ],
  },
  {
    label: 'Custom',
    options: [
      { key: 'custom', label: 'Custom URL…', url: '' },
    ],
  },
];

// Flat list with the preset URLs — used by the `url` memo above to
// resolve a key to its destination without walking the grouped tree
// on every render.
const URL_PRESETS = URL_PRESET_GROUPS.flatMap((g) => g.options);

/**
 * Browser-side mirror of the server's stripHtmlToPlain helper. Used for:
 *   - The "visible chars" counter under the editor (so headings + bullets
 *     don't inflate the visible count with their wrapping tags)
 *   - The push-notification preview card (since FCM payloads are text-only)
 *   - The history table's body column (older HTML rows shouldn't render
 *     raw tags in a 12px gray ellipsis line)
 *
 * Implemented via the DOM rather than regex — DOMParser is built-in,
 * handles entities correctly, and gives us a `textContent` reading that
 * matches what users will actually perceive.
 */
function stripHtmlClient(html) {
  if (!html) return '';
  if (typeof window === 'undefined' || !window.DOMParser) {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body?.textContent || '').replace(/\s+/g, ' ').trim();
  } catch {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}
